"use client";

import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useMemo,
} from "react";
import { fetchPricingPlanByKey } from "../actions/pricing";
import * as Sentry from "@sentry/nextjs";

// Types for our context
export interface PlanDetails {
  id: string | null;
  name: string | null;
  price: number | null;
  interval: "month" | "year" | null;
  trialDays: number | null;
  error?: string | null;
}

export interface EmbeddedCheckoutContextType {
  isLoading: boolean;
  error: string | null;
  monthlyPlan: PlanDetails;
  yearlyPlan: PlanDetails;
  isProcessingPayment: boolean;
  setIsProcessingPayment: (isProcessing: boolean) => void;
  isMonthlyPlan: boolean;
  setIsMonthlyPlan: (isMonthly: boolean) => void;
  currentPlan: PlanDetails;
}

// Create the context with a default value
const EmbeddedCheckoutContext =
  createContext<EmbeddedCheckoutContextType | null>(null);

/**
 * Custom hook to access the Embedded Checkout context.
 * Must be used within an EmbeddedCheckoutProvider component.
 */
export const useEmbeddedCheckoutContext = () => {
  const context = useContext(EmbeddedCheckoutContext);
  if (!context) {
    throw new Error(
      "useEmbeddedCheckoutContext must be used within an EmbeddedCheckoutProvider"
    );
  }
  return context;
};

export interface EmbeddedCheckoutProviderProps {
  children: React.ReactNode;
  pricingPlanKeys: {
    monthly: string;
    yearly: string;
  };
  defaultCadence?: "monthly" | "yearly";
  productName: string;
  trialDays?: number; // Override trial days from experiment
  /** SSR plans — when set, seeds state and skips the client-side pricing fetch. */
  initialPlanData?: {
    monthly: PlanDetails;
    yearly: PlanDetails;
  };
}

/**
 * EmbeddedCheckoutProvider - Fetches pricing plans from Stripe and manages
 * plan selection state (monthly/yearly toggle, trial days, loading/error).
 * Wraps the checkout UI so child components can access plan details via
 * useEmbeddedCheckoutContext().
 *
 * Despite the name, this provider is used with Stripe Elements (not Embedded
 * Checkout). It handles pricing plan fetching and cadence selection state.
 */
export function EmbeddedCheckoutProvider({
  children,
  pricingPlanKeys,
  defaultCadence = "monthly",
  trialDays: experimentTrialDays,
  initialPlanData,
}: EmbeddedCheckoutProviderProps) {
  const [isLoading, setIsLoading] = useState(!initialPlanData);
  const [error, setError] = useState<string | null>(null);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [isMonthlyPlan, setIsMonthlyPlan] = useState<boolean>(
    defaultCadence === "monthly"
  );

  const [monthlyPlan, setMonthlyPlan] = useState<PlanDetails>(
    initialPlanData?.monthly ?? {
      id: null,
      name: null,
      price: null,
      interval: null,
      trialDays: null,
    }
  );

  const [yearlyPlan, setYearlyPlan] = useState<PlanDetails>(
    initialPlanData?.yearly ?? {
      id: null,
      name: null,
      price: null,
      interval: null,
      trialDays: null,
    }
  );

  // Get current plan based on selection
  const currentPlan = useMemo(
    () => (isMonthlyPlan ? monthlyPlan : yearlyPlan),
    [isMonthlyPlan, monthlyPlan, yearlyPlan]
  );

  /**
   * Fetch pricing plans from Stripe API in parallel for better performance.
   * Skipped when initialPlanData was provided — state is already populated.
   */
  useEffect(() => {
    if (initialPlanData) return;
    let isMounted = true;

    const fetchPlans = async () => {
      try {
        setIsLoading(true);
        setError(null);

        Sentry.addBreadcrumb({
          message: "Fetching Stripe pricing plans for embedded checkout",
          category: "stripe.embedded",
          level: "info",
          data: {
            monthlyKey: pricingPlanKeys.monthly,
            yearlyKey: pricingPlanKeys.yearly,
          },
        });

        // Fetch both plans in parallel
        const [monthlyResult, yearlyResult] = await Promise.all([
          fetchPricingPlanByKey(pricingPlanKeys.monthly),
          fetchPricingPlanByKey(pricingPlanKeys.yearly),
        ]);

        if (!isMounted) return;

        // Update monthly plan state
        // Use experiment trial days if provided, otherwise use Stripe's trial days
        setMonthlyPlan({
          id: monthlyResult.id,
          name: monthlyResult.name,
          price: monthlyResult.unit_amount,
          interval: monthlyResult.interval as "month",
          trialDays:
            experimentTrialDays !== undefined
              ? experimentTrialDays
              : monthlyResult.trial_days,
        });

        // Update yearly plan state
        // Use experiment trial days if provided, otherwise use Stripe's trial days
        setYearlyPlan({
          id: yearlyResult.id,
          name: yearlyResult.name,
          price: yearlyResult.unit_amount,
          interval: yearlyResult.interval as "year",
          trialDays:
            experimentTrialDays !== undefined
              ? experimentTrialDays
              : yearlyResult.trial_days,
        });

        Sentry.addBreadcrumb({
          message: "Successfully fetched pricing plans",
          category: "stripe.embedded",
          level: "info",
          data: {
            monthlyPlanId: monthlyResult.id,
            yearlyPlanId: yearlyResult.id,
          },
        });

        setIsLoading(false);
      } catch (err) {
        if (!isMounted) return;

        const errorMessage =
          err instanceof Error
            ? err.message
            : "Failed to load pricing plans. Please refresh the page.";

        setError(errorMessage);
        setIsLoading(false);

        // Set error state in plans
        setMonthlyPlan((prev) => ({ ...prev, error: errorMessage }));
        setYearlyPlan((prev) => ({ ...prev, error: errorMessage }));

        Sentry.captureException(err, {
          tags: {
            component: "stripe.embedded.provider",
            operation: "fetch_pricing_plans",
          },
          extra: {
            monthlyKey: pricingPlanKeys.monthly,
            yearlyKey: pricingPlanKeys.yearly,
          },
        });
      }
    };

    fetchPlans();

    return () => {
      isMounted = false;
    };
  }, [pricingPlanKeys.monthly, pricingPlanKeys.yearly, initialPlanData]);

  const contextValue: EmbeddedCheckoutContextType = {
    isLoading,
    error,
    monthlyPlan,
    yearlyPlan,
    isProcessingPayment,
    setIsProcessingPayment,
    isMonthlyPlan,
    setIsMonthlyPlan,
    currentPlan,
  };

  return (
    <EmbeddedCheckoutContext.Provider value={contextValue}>
      {children}
    </EmbeddedCheckoutContext.Provider>
  );
}
